import * as Icons from 'lucide-react';
import type { WorkflowDetail } from '../types';
import { extractEnumInfo } from '../utils/schemaUtils';

interface SectionProps {
  workflow: WorkflowDetail;
}

export const InputsSection = ({ workflow }: SectionProps) => {
  const renderSchemaProperties = (schema: any) => {
    if (!schema || !schema.properties) {
      return null;
    }

    return (
      <div className="space-y-3">
        {Object.entries(schema.properties).map(([key, property]: [string, any]) => {
          const enumInfo = extractEnumInfo(property);

          return (
            <div
              key={key}
              className="flex items-start justify-between p-3 rounded-md bg-muted/20 border"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded text-zinc-900 dark:text-zinc-100">
                    {key}
                  </code>
                  {schema.required?.includes(key) && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                      required
                    </span>
                  )}
                  {enumInfo.defaultValue !== undefined && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                      default: {enumInfo.defaultValue}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {property.description || `${enumInfo.type || property.type || 'any'} type`}
                </p>
                {enumInfo.enum && (
                  <div className="mt-2">
                    <span className="text-xs text-muted-foreground">Options: </span>
                    {enumInfo.enum.map((option: string, index: number) => (
                      <code key={index} className="text-xs bg-muted px-1.5 py-0.5 rounded mr-1">
                        {option}
                      </code>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-xs text-muted-foreground ml-4">
                {enumInfo.type || property.type || 'any'}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="animate-in fade-in-50 duration-200">
      <section>
        <h2 className="text-xl font-semibold mb-6">Inputs & Outputs</h2>

        {/* Inputs */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Icons.ArrowDownToLine className="w-5 h-5 text-blue-600" />
              Workflow Inputs
            </h3>
            {workflow.inputSchema ? (
              <div className="p-4 md:p-6 rounded-lg border bg-card">
                <div className="mb-4">
                  <h4 className="text-base font-medium mb-2">Input Parameters</h4>
                  <p className="text-sm text-muted-foreground">
                    This workflow accepts the following input parameters:
                  </p>
                </div>
                {renderSchemaProperties(workflow.inputSchema)}
              </div>
            ) : (
              <div className="p-6 md:p-8 rounded-lg border bg-muted/30 text-center">
                <Icons.FileInput className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-muted-foreground" />
                <h4 className="text-lg font-medium mb-2">Input Parameters</h4>
                <p className="text-muted-foreground max-w-md mx-auto text-sm md:text-base">
                  Input schema information is not available for this workflow.
                </p>
              </div>
            )}
          </div>

          {/* Outputs */}
          <div>
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Icons.ArrowUpFromLine className="w-5 h-5 text-green-600" />
              Workflow Outputs
            </h3>
            {workflow.outputSchema ? (
              <div className="p-4 md:p-6 rounded-lg border bg-card">
                <div className="mb-4">
                  <h4 className="text-base font-medium mb-2">Output Schema</h4>
                  <p className="text-sm text-muted-foreground">
                    This workflow returns data in the following format:
                  </p>
                </div>
                {workflow.outputSchema.type === 'string' ? (
                  <div className="p-3 rounded-md bg-muted/20 border">
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-muted px-2 py-1 rounded">output</code>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Returns a string value</p>
                  </div>
                ) : (
                  renderSchemaProperties(workflow.outputSchema)
                )}
              </div>
            ) : (
              <div className="p-6 md:p-8 rounded-lg border bg-muted/30 text-center">
                <Icons.FileOutput className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-muted-foreground" />
                <h4 className="text-lg font-medium mb-2">Output Schema</h4>
                <p className="text-muted-foreground max-w-md mx-auto text-sm md:text-base">
                  Output schema information is not available for this workflow.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
